import { CircleCheck, CircleX, Clock, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
  cn,
} from '@sandtech-hub-monorepo/ui-kit';
import {
  BadgeStatus,
  CertificateStatus,
} from '@sandtech-hub-monorepo/ehub-client';
import {
  FIT_HELP_CENTER_URL,
  FitLogo,
  downloadFromUrl,
  formatDate,
  previewFromUrl,
} from '@sandtech-hub-monorepo/ui-kit-site';

import { CompactFooter } from '../../app/CompactFooter';
import { CredentialNotFound } from './CredentialNotFound';
import { RevokedCredentialBanner } from './RevokedCredentialBanner';

interface VerifiedCredential {
  /** "Program" or "Course", the eyebrow above the title. */
  subjectLabel: string;
  title: string;
  issuer: string;
  recipientName: string;
  issuedAt: string;
  expiresAt?: string | null;
  verificationCode: string;
  status: CertificateStatus | BadgeStatus;
  isValid: boolean;
  description: string;
  /** The large image: the rendered certificate, or the badge itself. */
  imageUrl?: string | null;
  downloadUrl?: string | null;
  /** Opened for viewing by "View in new tab"; defaults to imageUrl. */
  previewUrl?: string | null;
  /** Shown top right; the certificate doubles as the program badge. */
  badgeImageUrl?: string | null;
}

interface Props {
  kind: 'certificate' | 'badge';
  loading: boolean;
  credential?: VerifiedCredential | null;
}

// Keyed by status value; BadgeStatus and CertificateStatus share the same three.
const STATUS: Record<
  `${CertificateStatus}`,
  {
    label: string;
    headerLabel: string;
    Icon: typeof CircleCheck;
    className: string;
  }
> = {
  [CertificateStatus.Active]: {
    label: 'Valid',
    headerLabel: 'Verified',
    Icon: CircleCheck,
    className: 'bg-success/10 text-success',
  },
  [CertificateStatus.Revoked]: {
    label: 'Revoked',
    headerLabel: 'Revoked',
    Icon: CircleX,
    className: 'bg-destructive/10 text-destructive',
  },
  [CertificateStatus.Expired]: {
    label: 'Expired',
    headerLabel: 'Expired',
    Icon: Clock,
    className: 'bg-warning/10 text-warning',
  },
};

/**
 * Public credential verification page, reached without an account from the
 * gateway's /ob3/verify(-certificate)/:code redirect. Mirrors central-hub's
 * CertificateVerify / BadgeVerify. A revoked credential shows its details and
 * status only: no download or view actions (a certificate's files are also
 * hidden by the API).
 */
export const CredentialVerifyView = ({ kind, loading, credential }: Props) => {
  // An ACTIVE credential past its expiry date is not valid, but its stored
  // status stays ACTIVE (the API reports that through isValid).
  const status =
    credential &&
    STATUS[
      credential.status === CertificateStatus.Active && !credential.isValid
        ? CertificateStatus.Expired
        : credential.status
    ];
  const revoked = credential?.status === CertificateStatus.Revoked;
  const downloadUrl = credential?.downloadUrl;

  return (
    <div className="flex min-h-screen flex-col">
      <div className="flex flex-1 flex-col bg-muted/30 px-4 py-6 sm:px-10">
        <div className="mx-auto flex w-full max-w-screen-2xl flex-1 flex-col gap-4 rounded-2xl bg-background p-4">
          <header className="flex items-center justify-between border-b border-border pb-4">
            <div className="flex items-center gap-7">
              <FitLogo className="h-8 w-auto" />
              {!loading && !credential && (
                <span className="text-sm text-muted-foreground">
                  Digital {kind === 'badge' ? 'Badge' : 'Certificate'}
                </span>
              )}
            </div>
            {!loading && status && (
              <span
                className={cn(
                  'flex items-center gap-1 rounded-md px-2 py-0.5 text-sm',
                  status.className
                )}
              >
                <status.Icon className="h-3.5 w-3.5" />
                {status.headerLabel}
              </span>
            )}
          </header>

          {loading && (
            <div className="flex items-center justify-center gap-2 py-24 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
              Verifying credential…
            </div>
          )}

          {!loading && !credential && <CredentialNotFound noun={kind} />}

          {!loading && credential && status && (
            <>
              {revoked && <RevokedCredentialBanner noun={kind} />}
              <div className="flex flex-col gap-8 p-2 sm:p-6 lg:flex-row lg:gap-10">
                <div className="flex w-full shrink-0 flex-col gap-6 lg:w-2/5">
                  {credential.imageUrl ? (
                    <img
                      src={credential.imageUrl}
                      alt={credential.title}
                      className={cn(
                        'rounded-lg',
                        kind === 'certificate'
                          ? 'w-full'
                          : 'mx-auto h-auto w-2/3 max-w-xs'
                      )}
                    />
                  ) : (
                    <div className="flex aspect-video w-full items-center justify-center rounded-lg bg-muted p-6 text-center text-sm text-muted-foreground">
                      This {kind} is no longer available to view.
                    </div>
                  )}
                  {credential.imageUrl && !revoked && (
                    <div className="flex flex-wrap gap-4">
                      {downloadUrl && (
                        <Button
                          variant="outline"
                          className="h-12 flex-1 basis-40"
                          onClick={() =>
                            downloadFromUrl(
                              // Keeps the file's own extension (.pdf certificate, .png badge).
                              `${credential.title} ${kind}.${new URL(
                                downloadUrl
                              ).pathname
                                .split('.')
                                .pop()}`,
                              downloadUrl
                            )
                          }
                        >
                          Download {kind === 'badge' ? 'Badge' : 'Certificate'}
                        </Button>
                      )}
                      <Button
                        className="h-12 flex-1 basis-40"
                        onClick={() =>
                          previewFromUrl(
                            credential.previewUrl ?? credential.imageUrl ?? ''
                          )
                        }
                      >
                        View in new tab
                      </Button>
                    </div>
                  )}
                </div>

                <dl className="flex min-w-0 flex-1 flex-col gap-4 text-sm">
                  <div>
                    <dt className="text-muted-foreground">
                      {credential.subjectLabel}
                    </dt>
                    <dd className="text-2xl font-medium text-heading">
                      {credential.title}
                    </dd>
                  </div>
                  {[
                    ['Issued From', credential.issuer],
                    ['Issued to', credential.recipientName],
                    ['Issued on', formatDate(credential.issuedAt)],
                    credential.expiresAt && [
                      'Expiry Date',
                      formatDate(credential.expiresAt),
                    ],
                    ['ID', credential.verificationCode],
                  ]
                    .filter((row): row is string[] => !!row)
                    .map(([term, value]) => (
                      <div key={term}>
                        <dt className="text-muted-foreground">{term}</dt>
                        <dd className="break-all text-heading">{value}</dd>
                      </div>
                    ))}
                  <div className="flex flex-col items-start gap-1">
                    <dt className="text-muted-foreground">Status</dt>
                    <dd
                      className={cn(
                        'flex items-center gap-1 rounded-md px-2 py-0.5',
                        status.className
                      )}
                    >
                      <status.Icon className="h-3.5 w-3.5" />
                      {status.label}
                    </dd>
                  </div>
                </dl>

                {credential.badgeImageUrl && (
                  <img
                    src={credential.badgeImageUrl}
                    alt=""
                    className="h-auto w-20 self-start sm:w-24 lg:w-28"
                  />
                )}
              </div>

              <Accordion
                type="single"
                collapsible
                defaultValue="about"
                className="rounded-lg border border-border px-6"
              >
                <AccordionItem value="about" className="border-none">
                  <AccordionTrigger className="font-medium text-heading">
                    About this {kind}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">
                    {credential.description}
                  </AccordionContent>
                </AccordionItem>
              </Accordion>

              <Link
                to={FIT_HELP_CENTER_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-primary"
              >
                Something wrong with this?
              </Link>
            </>
          )}
        </div>
      </div>
      <CompactFooter />
    </div>
  );
};
